import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MechanicListQuery } from "./client";
import { mechanicsApi } from "./client";

export const mechanicsKeys = {
  all: ["mechanics"] as const,
  list: (q: MechanicListQuery) => ["mechanics", "list", q] as const,
  stats: ["mechanics", "stats"] as const,
  detail: (id: string) => ["mechanics", "detail", id] as const,
  performance: (id: string, period: "day" | "week" | "month") =>
    ["mechanics", "performance", id, period] as const,
};

export function useMechanicsQuery(query: MechanicListQuery = {}) {
  return useQuery({
    queryKey: mechanicsKeys.list(query),
    queryFn: () => mechanicsApi.list(query),
  });
}

export function useMechanicStatsQuery() {
  return useQuery({
    queryKey: mechanicsKeys.stats,
    queryFn: () => mechanicsApi.stats(),
  });
}

export function useMechanicQuery(id: string) {
  return useQuery({
    queryKey: mechanicsKeys.detail(id),
    queryFn: () => mechanicsApi.getById(id),
    enabled: !!id,
  });
}

export function useMechanicPerformanceQuery(
  id: string,
  period: "day" | "week" | "month" = "week",
) {
  return useQuery({
    queryKey: mechanicsKeys.performance(id, period),
    queryFn: () => mechanicsApi.getPerformance(id, period),
    enabled: !!id,
  });
}

function useMechanicMutations(id: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: mechanicsKeys.detail(id) });
    qc.invalidateQueries({ queryKey: mechanicsKeys.all });
  };

  const verify = useMutation({
    mutationFn: () => mechanicsApi.verify(id),
    onSuccess: () => {
      invalidate();
      toast.success("ຢືນຢັນຕົວຕົນສຳເລັດ");
    },
    onError: () => toast.error("ບໍ່ສາມາດຢືນຢັນໄດ້"),
  });

  const reject = useMutation({
    mutationFn: (body: { reason?: string }) => mechanicsApi.reject(id, body),
    onSuccess: () => {
      invalidate();
      toast.success("ປະຕິເສດຄຳຮ້ອງສຳເລັດ");
    },
    onError: () => toast.error("ບໍ່ສາມາດປະຕິເສດໄດ້"),
  });

  const suspend = useMutation({
    mutationFn: (body: { reason?: string }) => mechanicsApi.suspend(id, body),
    onSuccess: () => {
      invalidate();
      toast.success("ລະງັບບັນຊີສຳເລັດ");
    },
    onError: () => toast.error("ບໍ່ສາມາດລະງັບໄດ້"),
  });

  const unsuspend = useMutation({
    mutationFn: (body: { reason?: string } = {}) =>
      mechanicsApi.unsuspend(id, body),
    onSuccess: () => {
      invalidate();
      toast.success("ຍົກເລີກລະງັບສຳເລັດ");
    },
    onError: () => toast.error("ບໍ່ສາມາດຍົກເລີກລະງັບໄດ້"),
  });

  return { verify, reject, suspend, unsuspend };
}

export { useMechanicMutations };
